import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { HelpCircle, Plus, MessageCircle, Clock, CheckCircle, Loader2, Send, AlertCircle } from "lucide-react";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { useAuth } from "../App";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const statusColors = {
  open: "bg-neon-cyan/20 text-neon-cyan border-neon-cyan/50",
  "in-progress": "bg-amber-500/20 text-amber-400 border-amber-500/50",
  resolved: "bg-neon-green/20 text-neon-green border-neon-green/50",
  closed: "bg-zinc-500/20 text-zinc-400 border-zinc-500/50"
};

const SupportPage = () => {
  const { user } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [replying, setReplying] = useState(false);
  const [form, setForm] = useState({ subject: "", message: "", category: "general" });

  const getAuthHeaders = () => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  useEffect(() => {
    if (user) fetchTickets();
    else setLoading(false);
  }, [user]);

  const fetchTickets = async () => {
    try {
      const res = await axios.get(`${API}/tickets/`, { headers: getAuthHeaders(), withCredentials: true });
      setTickets(res.data);
    } catch (e) {} finally { setLoading(false); }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const res = await axios.post(`${API}/tickets/`, form, { headers: getAuthHeaders(), withCredentials: true });
      toast.success("Ticket created!");
      setShowCreate(false);
      setForm({ subject: "", message: "", category: "general" });
      fetchTickets();
      setSelectedTicket(res.data);
    } catch (e) { toast.error("Failed to create ticket"); }
    finally { setCreating(false); }
  };

  const handleReply = async (e) => {
    e.preventDefault();
    if (!replyText.trim() || !selectedTicket) return;
    setReplying(true);
    try {
      const res = await axios.post(`${API}/tickets/${selectedTicket.ticket_id}/reply`, { message: replyText }, { headers: getAuthHeaders(), withCredentials: true });
      setSelectedTicket(res.data);
      setReplyText("");
      fetchTickets();
    } catch (e) { toast.error("Failed to send reply"); }
    finally { setReplying(false); }
  };

  const loadTicket = async (ticket) => {
    try {
      const res = await axios.get(`${API}/tickets/${ticket.ticket_id}`, { headers: getAuthHeaders(), withCredentials: true });
      setSelectedTicket(res.data);
    } catch (e) { setSelectedTicket(ticket); }
  };

  if (!user) return (
    <div className="min-h-screen flex items-center justify-center px-6">
      <div className="glass p-12 text-center max-w-lg">
        <HelpCircle className="w-16 h-16 text-neon-cyan mx-auto mb-6" />
        <h1 className="font-heading text-3xl uppercase mb-4">Support Center</h1>
        <p className="text-zinc-400">Please sign in to access support.</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen py-10 px-6" data-testid="support-page">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <Badge className="bg-sky-500/20 text-sky-400 border-sky-500/50 mb-2">Help</Badge>
            <h1 className="font-heading text-3xl md:text-4xl uppercase tracking-tight">Support Center</h1>
          </div>
          <Dialog open={showCreate} onOpenChange={setShowCreate}>
            <DialogTrigger asChild>
              <Button className="bg-neon-cyan text-black font-bold" data-testid="create-ticket-button">
                <Plus className="w-4 h-4 mr-2" />New Ticket
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-zinc-900 border-white/10 max-w-lg">
              <DialogHeader><DialogTitle className="font-heading">Create Support Ticket</DialogTitle></DialogHeader>
              <form onSubmit={handleCreate} className="space-y-4">
                <div>
                  <Label>Category</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger className="bg-black/50 border-white/10"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-white/10">
                      <SelectItem value="general">General</SelectItem>
                      <SelectItem value="order">Order Issue</SelectItem>
                      <SelectItem value="payment">Payment</SelectItem>
                      <SelectItem value="technical">Technical</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Subject</Label>
                  <Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="bg-black/50 border-white/10" required placeholder="Brief description of your issue" data-testid="ticket-subject" />
                </div>
                <div>
                  <Label>Message</Label>
                  <Textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="bg-black/50 border-white/10" required rows={4} placeholder="Describe your issue in detail..." data-testid="ticket-message" />
                </div>
                <Button type="submit" disabled={creating} className="w-full bg-neon-cyan text-black font-bold" data-testid="submit-ticket">
                  {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}Submit Ticket
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Tickets list */}
          <div className="space-y-3">
            {loading ? (
              <div className="glass p-8 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-neon-cyan" /></div>
            ) : tickets.length === 0 ? (
              <div className="glass p-8 text-center">
                <MessageCircle className="w-10 h-10 text-zinc-600 mx-auto mb-3" />
                <p className="text-zinc-500 text-sm">No tickets yet</p>
              </div>
            ) : tickets.map((ticket) => (
              <button
                key={ticket.ticket_id}
                onClick={() => loadTicket(ticket)}
                className={`w-full text-left glass p-4 transition-colors hover:bg-white/5 ${selectedTicket?.ticket_id === ticket.ticket_id ? 'border border-neon-cyan/50' : ''}`}
                data-testid={`ticket-item-${ticket.ticket_id}`}
              >
                <div className="flex items-center justify-between mb-1">
                  <p className="font-medium text-sm truncate flex-1">{ticket.subject}</p>
                  <Badge className={`text-[10px] ml-2 ${statusColors[ticket.status] || statusColors.open}`}>{ticket.status}</Badge>
                </div>
                <p className="text-xs text-zinc-600 truncate">{ticket.category} | {new Date(ticket.created_at).toLocaleDateString()}</p>
                <p className="text-xs text-zinc-500 mt-1">{ticket.messages?.length || 0} messages</p>
              </button>
            ))}
          </div>

          {/* Ticket detail */}
          <div className="md:col-span-2">
            {selectedTicket ? (
              <div className="glass p-6" data-testid="ticket-detail">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h2 className="font-heading text-lg">{selectedTicket.subject}</h2>
                    <p className="text-xs text-zinc-500">{selectedTicket.category} | Created {new Date(selectedTicket.created_at).toLocaleString()}</p>
                  </div>
                  <Badge className={statusColors[selectedTicket.status] || statusColors.open}>{selectedTicket.status}</Badge>
                </div>

                <div className="space-y-4 mb-6 max-h-96 overflow-y-auto pr-2">
                  {selectedTicket.messages?.map((msg, idx) => (
                    <motion.div
                      key={idx}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`p-4 rounded-sm ${msg.sender === 'admin' ? 'bg-neon-cyan/10 border border-neon-cyan/20 ml-4' : 'bg-white/5 border border-white/10 mr-4'}`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className={`text-xs font-bold uppercase ${msg.sender === 'admin' ? 'text-neon-cyan' : 'text-zinc-400'}`}>
                          {msg.sender === 'admin' ? 'Support Team' : msg.sender_name || 'You'}
                        </span>
                        <span className="text-[10px] text-zinc-600">{new Date(msg.created_at).toLocaleString()}</span>
                      </div>
                      <p className="text-sm text-zinc-300 whitespace-pre-wrap">{msg.message}</p>
                    </motion.div>
                  ))}
                </div>

                {selectedTicket.status !== "closed" && (
                  <form onSubmit={handleReply} className="flex gap-2">
                    <Input
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      placeholder="Type your reply..."
                      className="bg-black/50 border-white/10 flex-1"
                      data-testid="ticket-reply-input"
                    />
                    <Button type="submit" disabled={replying || !replyText.trim()} className="bg-neon-cyan text-black" data-testid="send-reply-button">
                      {replying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    </Button>
                  </form>
                )}
              </div>
            ) : (
              <div className="glass p-12 text-center">
                <AlertCircle className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
                <p className="text-zinc-500">Select a ticket to view details, or create a new one.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SupportPage;
